import { styled } from "@mui/material";
import { createContext, ReactNode, useContext } from "react";

import { TEST_IDS } from "constants/TestIds";
import { useActive } from "hooks/useActive";

type TreeGroupContext = { isGroupExpanded: boolean };

const Context = createContext<TreeGroupContext>({
  isGroupExpanded: false,
});

const TreeGroupContextProvider = Context.Provider;

export function useTreeGroupContext() {
  const data = useContext(Context);

  if (!data) {
    throw new Error(
      "useTreeGroupContext hook should be wrapped by TreeGroupContextProvider"
    );
  }

  return data;
}

export interface TreeGroupProps {
  children: ReactNode;
  label: ReactNode;
  "data-testid"?: string;
}

export function TreeGroup({
  children,
  label,
  "data-testid": dataTestId,
}: TreeGroupProps) {
  const { isActive, toggleActive } = useActive();

  return (
    <TreeGroupContextProvider value={{ isGroupExpanded: isActive }}>
      <details
        data-testid={dataTestId}
        open={isActive}
        onClick={(event) => event.preventDefault()}
      >
        <HeaderWrapper onClick={toggleActive}>{label}</HeaderWrapper>
        <TreeItemsWrapper
          data-testid={dataTestId && TEST_IDS.getItems(dataTestId)}
        >
          {children}
        </TreeItemsWrapper>
      </details>
    </TreeGroupContextProvider>
  );
}

const HeaderWrapper = styled("summary")(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(1.5, 1.5, 1.5, 0),
  borderRadius: 4,
  overflowX: "auto",
  cursor: "pointer",
  transition: "box-shadow .3s ease-out, background-color .3s ease-out",
  alignItems: "center",

  "&::-webkit-details-marker": {
    display: "none",
  },

  "&:focus": {
    outline: "none",
  },
}));

const TreeItemsWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginLeft: theme.spacing(2.5),
}));
